import { AxiosError } from 'axios';
import { createAdminGatewayApi } from './gatewayApi';
import type {
  AdminAuthResponse,
  AdminSession,
  AdminSessionProjection,
  AdminTotpStartResponse,
  AdminUserSummary,
  AdminUsersResponse,
  Page,
  RoleAssignment,
  RoleCatalogEntry,
  RoleReplacement,
  SecurityEvent,
  SecurityMutation,
  StatusMutation,
} from '../types/adminAuthRedesign';

const ADMIN_BASE = '/api/v2/admin';
const CLIENT_HEADERS = {
  'X-TripAlta-Client': 'admin',
  'X-Client-Platform': 'ADMIN_WEB',
} as const;

export interface AdminRedesignError {
  code: string;
  message: string;
  correlationId?: string;
  status?: number;
}

export const adminAuthRedesignApi = createAdminGatewayApi();

export function normalizeAdminRedesignError(
  error: AxiosError
): AdminRedesignError {
  const body = error.response?.data as
    | {
        code?: string;
        error?: string;
        message?: string;
        correlationId?: string;
      }
    | undefined;
  return {
    code: body?.code ?? body?.error ?? 'AUTH_SERVICE_UNAVAILABLE',
    message:
      body?.message ??
      (error.response
        ? 'The request could not be completed.'
        : 'The authentication service is temporarily unavailable.'),
    correlationId: body?.correlationId,
    status: error.response?.status,
  };
}

adminAuthRedesignApi.interceptors.response.use(
  response => response,
  (error: AxiosError) => Promise.reject(normalizeAdminRedesignError(error))
);

function authHeaders(deviceName?: string) {
  return {
    ...CLIENT_HEADERS,
    ...(deviceName ? { 'X-Device-Name': deviceName } : {}),
  };
}

export async function passwordLogin(
  email: string,
  password: string,
  deviceName?: string
): Promise<AdminAuthResponse> {
  const { data } = await adminAuthRedesignApi.post<AdminAuthResponse>(
    `${ADMIN_BASE}/auth/password/login`,
    { email, password },
    { headers: authHeaders(deviceName) }
  );
  return data;
}

export async function completeLoginMfa(
  challengeId: string,
  code: string
): Promise<AdminAuthResponse> {
  const { data } = await adminAuthRedesignApi.post<AdminAuthResponse>(
    `${ADMIN_BASE}/auth/mfa/challenge/complete`,
    { challengeId, code },
    { headers: authHeaders() }
  );
  return data;
}

export async function startFirstTotp(
  transactionId: string
): Promise<AdminTotpStartResponse> {
  const { data } = await adminAuthRedesignApi.post<AdminTotpStartResponse>(
    `${ADMIN_BASE}/auth/mfa/totp/start`,
    { transactionId },
    { headers: authHeaders() }
  );
  return data;
}

export async function completeFirstTotp(
  transactionId: string,
  code: string,
  name = 'TripAlta Admin'
): Promise<AdminAuthResponse> {
  const { data } = await adminAuthRedesignApi.post<AdminAuthResponse>(
    `${ADMIN_BASE}/auth/mfa/totp/complete`,
    { transactionId, code, name },
    { headers: authHeaders() }
  );
  return data;
}

export async function confirmRecoveryCodesSaved(
  transactionId: string
): Promise<AdminAuthResponse> {
  const { data } = await adminAuthRedesignApi.post<AdminAuthResponse>(
    `${ADMIN_BASE}/auth/mfa/recovery-codes/saved`,
    { transactionId, saved: true },
    { headers: authHeaders() }
  );
  return data;
}

export async function getAdminSession(): Promise<AdminSessionProjection> {
  const { data } = await adminAuthRedesignApi.get<AdminSessionProjection>(
    `${ADMIN_BASE}/auth/session`
  );
  return data;
}

export async function logoutAdminSession(): Promise<void> {
  await adminAuthRedesignApi.post(`${ADMIN_BASE}/auth/logout`);
}

export async function logoutAllAdminSessions(): Promise<void> {
  await adminAuthRedesignApi.post(`${ADMIN_BASE}/auth/logout/all`);
}

export async function listUsers(params: {
  search?: string;
  accountStatus?: string;
  page?: number;
  size?: number;
  sort?: string;
}): Promise<AdminUsersResponse> {
  const { data } = await adminAuthRedesignApi.get<AdminUsersResponse>(
    `${ADMIN_BASE}/users`,
    { params }
  );
  return data;
}

export async function getUser(userId: string): Promise<AdminUserSummary> {
  const { data } = await adminAuthRedesignApi.get<AdminUserSummary>(
    `${ADMIN_BASE}/users/${encodeURIComponent(userId)}`
  );
  return data;
}

export async function getSessions(userId: string): Promise<AdminSession[]> {
  const { data } = await adminAuthRedesignApi.get<{
    sessions: AdminSession[];
    count: number;
  }>(`${ADMIN_BASE}/users/${encodeURIComponent(userId)}/sessions`);
  return data.sessions;
}

export async function getSecurityEvents(
  userId: string,
  page = 0,
  size = 25
): Promise<Page<SecurityEvent>> {
  const { data } = await adminAuthRedesignApi.get<Page<SecurityEvent>>(
    `${ADMIN_BASE}/users/${encodeURIComponent(userId)}/security-events`,
    { params: { page, size } }
  );
  return data;
}

export async function getRoleCatalog(): Promise<RoleCatalogEntry[]> {
  const { data } = await adminAuthRedesignApi.get<{
    roles: RoleCatalogEntry[];
  }>(`${ADMIN_BASE}/roles`);
  return data.roles;
}

export async function getUserRoles(userId: string): Promise<RoleAssignment> {
  const { data } = await adminAuthRedesignApi.get<RoleAssignment>(
    `${ADMIN_BASE}/users/${encodeURIComponent(userId)}/roles`
  );
  return data;
}

export async function revokeSession(
  userId: string,
  sessionId: string,
  mutation: SecurityMutation
): Promise<void> {
  await adminAuthRedesignApi.delete(
    `${ADMIN_BASE}/users/${encodeURIComponent(userId)}/sessions/${encodeURIComponent(sessionId)}`,
    { data: mutation }
  );
}

export async function revokeAllSessions(
  userId: string,
  mutation: SecurityMutation
): Promise<void> {
  await adminAuthRedesignApi.delete(
    `${ADMIN_BASE}/users/${encodeURIComponent(userId)}/sessions`,
    { data: mutation }
  );
}

export async function initiatePasswordReset(
  userId: string,
  mutation: SecurityMutation
): Promise<void> {
  await adminAuthRedesignApi.post(
    `${ADMIN_BASE}/users/${encodeURIComponent(userId)}/password-reset`,
    mutation
  );
}

export async function requireMfaReenrollment(
  userId: string,
  mutation: SecurityMutation
): Promise<void> {
  await adminAuthRedesignApi.post(
    `${ADMIN_BASE}/users/${encodeURIComponent(userId)}/mfa/reenrollment`,
    mutation
  );
}

export async function transitionUserStatus(
  userId: string,
  mutation: StatusMutation
): Promise<void> {
  await adminAuthRedesignApi.post(
    `${ADMIN_BASE}/users/${encodeURIComponent(userId)}/status`,
    mutation
  );
}

export async function replaceUserRoles(
  userId: string,
  mutation: RoleReplacement
): Promise<RoleAssignment> {
  const { data } = await adminAuthRedesignApi.put<RoleAssignment>(
    `${ADMIN_BASE}/users/${encodeURIComponent(userId)}/roles`,
    mutation
  );
  return data;
}

export interface AdminDeletionStatus {
  requestId: string | null;
  userId: string;
  status: string;
  scheduledDeletionAt: string | null;
  completedAt: string | null;
  attempts: number;
  failedStep: string | null;
  nextAttemptAt: string | null;
  completedSteps: string[];
}
export interface AdminDeletionMutation {
  mode: 'PERMANENT_DELETE' | 'DELETE_NOW';
  expectedVersion: number;
  reasonCode: string;
  idempotencyKey: string;
  confirmationText: string;
}
export async function getDeletionStatus(
  userId: string
): Promise<AdminDeletionStatus> {
  const { data } = await adminAuthRedesignApi.get<AdminDeletionStatus>(
    `${ADMIN_BASE}/users/${encodeURIComponent(userId)}/deletion`
  );
  return data;
}
export async function deleteAccount(
  userId: string,
  mutation: AdminDeletionMutation
): Promise<AdminDeletionStatus> {
  const { data } = await adminAuthRedesignApi.post<AdminDeletionStatus>(
    `${ADMIN_BASE}/users/${encodeURIComponent(userId)}/deletion`,
    mutation
  );
  return data;
}
